import { Button } from '@components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@components/ui/card'
import { Switch } from '@components/ui/switch'
import { useQuery } from '@connectrpc/connect-query'
import { useAuthenticatedMutation } from '@context/sessions'
import { parseErrorMessage } from '@lib/errors'
import { cn } from '@lib/utils'
import { Label } from '@radix-ui/react-label'
import { Eye, EyeClosed, EyeOff, TriangleAlert } from 'lucide-react'
import React, { FC, FormEvent, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  getMyContentSettings,
  updateMyContentSettings,
} from '@gen/api/v1/api-ApiService_connectquery'
import Title from '@components/title'

const ContentSettingsView: FC = () => {
  const { data: settingsRes, refetch: refetchSettings } =
    useQuery(getMyContentSettings)
  const updateMyContentSettingsMutation = useAuthenticatedMutation(
    updateMyContentSettings,
  )

  const [hidePotentiallySensitiveContent, setHidePotentiallySensitiveContent] =
    useState<boolean>(false)
  const [showSensitiveContentWarning, setShowSensitiveContentWarning] =
    useState<boolean>(false)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      await updateMyContentSettingsMutation.mutateAsync({
        hidePotentiallySensitiveContent,
        showSensitiveContentWarning,
      })

      const { data: settingsRes } = await refetchSettings()

      if (settingsRes) {
        setHidePotentiallySensitiveContent(
          settingsRes.hidePotentiallySensitiveContent,
        )
        setShowSensitiveContentWarning(settingsRes.showSensitiveContentWarning)
      }

      toast.success('Settings updated successfully')
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to update settings', {
        description: message,
      })
    }
  }

  useEffect(() => {
    if (settingsRes) {
      setHidePotentiallySensitiveContent(
        settingsRes.hidePotentiallySensitiveContent,
      )
      setShowSensitiveContentWarning(settingsRes.showSensitiveContentWarning)
    }
  }, [settingsRes])

  return (
    <div className="max-w-3xl mx-auto pb-16">
      <Title title="Content settings" />

      <div>
        <h1 className="text-3xl font-semibold flex items-center">
          <EyeOff className="mr-2" strokeWidth={3} />
          Content settings
        </h1>
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Sensitive content settings</CardTitle>
          <CardDescription>
            Control how sensitive content is displayed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2">
              <Label className="font-semibold">
                <div className="flex items-center">
                  {hidePotentiallySensitiveContent ? (
                    <EyeClosed className="h-4 mr-2 w-4" strokeWidth={3} />
                  ) : (
                    <Eye className="h-4 mr-2 w-4" strokeWidth={3} />
                  )}
                  Hide potentially sensitive content
                </div>
                <div className="font-normal text-muted-foreground text-sm mt-2">
                  {hidePotentiallySensitiveContent
                    ? 'We will not show posts that have been deemed potentially sensitive.'
                    : 'We will show all posts, regardless of whether they have been deemed potentially sensitive.'}
                </div>
              </Label>
              <div className="text-right">
                <Switch
                  checked={hidePotentiallySensitiveContent}
                  onCheckedChange={setHidePotentiallySensitiveContent}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 mt-6 pt-6 border-t">
              <Label
                className={cn(
                  'font-semibold',
                  hidePotentiallySensitiveContent && 'text-muted-foreground',
                )}
              >
                <div className="flex items-center">
                  <TriangleAlert className="h-4 mr-2 w-4" strokeWidth={3} />
                  Show sensitive content warning
                </div>
                <div className="font-normal text-muted-foreground text-sm mt-2">
                  <>
                    {!!showSensitiveContentWarning ||
                    !!hidePotentiallySensitiveContent
                      ? 'We will show a warning before displaying any potentially sensitive content.'
                      : 'We will not show a warning before displaying any potentially sensitive content.'}
                  </>
                </div>
              </Label>
              <div className="text-right">
                <Switch
                  checked={
                    !hidePotentiallySensitiveContent &&
                    showSensitiveContentWarning
                  }
                  disabled={hidePotentiallySensitiveContent}
                  onCheckedChange={setShowSensitiveContentWarning}
                />
              </div>
            </div>

            <div className="mt-8 text-right">
              <Button
                disabled={
                  hidePotentiallySensitiveContent ===
                    settingsRes?.hidePotentiallySensitiveContent &&
                  showSensitiveContentWarning ===
                    settingsRes?.showSensitiveContentWarning
                }
                type="submit"
              >
                Save changes
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export default ContentSettingsView
